import { useCallback } from 'react';
import { isType } from 'variant';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import type { AppComponents } from '@wix/dev-center-client';
import type { CustomElement } from '@wix/cli-app-manifest';
import { useProjectModel } from '../../model.js';
import { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';
import { createCustomElementConfig } from '../../app-manifest/create.js';

// You can find dummy-custom-element codebase at https://github.com/wix-private/wix-cli-internal/tree/9b2a26e76375313eabb735df459944f67d7002d7/packages/wix-cli-dummy-custom-element
const DUMMY_CUSTOM_ELEMENT_TAG_NAME = 'wix-cli-dummy-custom-element';
const DUMMY_CUSTOM_ELEMENT_URL =
  'https://www.unpkg.com/@wix/cli-dummy-custom-element';
const DUMMY_CUSTOM_ELEMENT_THUMBNAIL_URL =
  'https://www.unpkg.com/@wix/cli-dummy-custom-element/dist/preset/thumbnail.png';

export function useCustomElementsMigration() {
  const { model } = useProjectModel();

  const getNewElements = useCallback(
    (newCustomElements: Array<DevCenterComponent<'CustomElement'>>) => {
      return newCustomElements.map<CustomElement>((customElement) => {
        const config = createCustomElementConfig(
          customElement,
          DUMMY_CUSTOM_ELEMENT_URL
        );

        return {
          ...config,
          compData: {
            customElementWidget: {
              ...config.compData.customElementWidget,
              widgetData: {
                ...config.compData.customElementWidget.widgetData,
                tagName: DUMMY_CUSTOM_ELEMENT_TAG_NAME,
              },
              presets: config.compData.customElementWidget.presets?.map(
                (preset) => ({
                  ...preset,
                  thumbnailUrl: DUMMY_CUSTOM_ELEMENT_THUMBNAIL_URL,
                })
              ),
            },
          },
        };
      });
    },
    []
  );

  return useCallback(
    (appComponents: AppComponents) => {
      const localCustomElements = model.components.filter((component) =>
        isType(component, DevCenterComponent.CustomElement)
      );

      if (!localCustomElements.length) {
        return;
      }

      const existingCustomElements = appComponents.filter(
        (component) =>
          component.compType === ComponentType.CUSTOM_ELEMENT_WIDGET
      );

      const newCustomElements = localCustomElements.filter(
        (localCustomElement) =>
          !existingCustomElements.some(
            (customElement) => customElement.compId === localCustomElement.id
          )
      );

      if (!newCustomElements.length) {
        return;
      }

      return getNewElements(newCustomElements);
    },
    [getNewElements, model.components]
  );
}
